// pages/ExpensesPage.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const apiUrl = process.env.REACT_APP_API_URL;
const userId = localStorage.getItem('userId');

const ExpensesPage = () => {
  const { t } = useTranslation();
  const [expenses, setExpenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    expense_type: '',
    description: '',
    amount: '',
    expense_date: '',
    paid_to: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchExpenses = async () => {
    try {
      const res = await axios.get(`${apiUrl}/expenses`);
      // تأكد أن البيانات مصفوفة، وإلا عيّنها كمصفوفة فارغة
      setExpenses(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setExpenses([]); // fallback لضمان أنها مصفوفة
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${apiUrl}/expenses/${editing.expense_id}`, {
          ...formData,
          modified_by: userId,
        });
        setSnackbar({
          open: true,
          message: t('updated_successfully'),
          severity: 'success',
        });
      } else {
        await axios.post(`${apiUrl}/expenses`, {
          ...formData,
          created_by: userId,
        });
        setSnackbar({
          open: true,
          message: t('saved_successfully'),
          severity: 'success',
        });
      }
      setOpen(false);
      setEditing(null);
      fetchExpenses();
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: t('error_occurred'),
        severity: 'error',
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${apiUrl}/expenses/${id}`, {
        data: { deleted_by: userId },
      });
      setSnackbar({
        open: true,
        message: t('deleted_successfully'),
        severity: 'info',
      });
      fetchExpenses();
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: t('error_occurred'),
        severity: 'error',
      });
    }
  };

  const openForm = (expense = null) => {
    setEditing(expense);
    setFormData(
      expense
        ? {
            expense_type: expense.expense_type,
            description: expense.description,
            amount: expense.amount,
            expense_date: expense.expense_date?.slice(0, 10),
            paid_to: expense.paid_to,
          }
        : {
            expense_type: '',
            description: '',
            amount: '',
            expense_date: '',
            paid_to: '',
          }
    );
    setOpen(true);
  };

  return (
    <Box p={4}>
      <Typography variant="h4" align="center">
        {t('expenses')}
      </Typography>

      <Box textAlign="right" mb={2}>
        <Button variant="contained" onClick={() => openForm()}>
          {t('add_expense')}
        </Button>
      </Box>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('type')}</TableCell>
              <TableCell>{t('description')}</TableCell>
              <TableCell>{t('amount')}</TableCell>
              <TableCell>{t('date')}</TableCell>
              <TableCell>{t('paid_to')}</TableCell>
              <TableCell>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses.map((exp) => (
              <TableRow key={exp.expense_id}>
                <TableCell>{exp.expense_type}</TableCell>
                <TableCell>{exp.description}</TableCell>
                <TableCell>{exp.amount}</TableCell>
                <TableCell>
                  {new Date(exp.expense_date).toLocaleDateString()}
                </TableCell>
                <TableCell>{exp.paid_to}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => openForm(exp)}>
                    {t('edit')}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleDelete(exp.expense_id)}
                  >
                    {t('delete')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Dialog Form */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing ? t('edit_expense') : t('add_expense')}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('type')}
            value={formData.expense_type}
            onChange={(e) => handleChange('expense_type', e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('description')}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('amount')}
            value={formData.amount}
            onChange={(e) => handleChange('amount', e.target.value)}
            margin="normal"
            type="number"
          />
          <TextField
            fullWidth
            label={t('date')}
            value={formData.expense_date}
            onChange={(e) => handleChange('expense_date', e.target.value)}
            margin="normal"
            type="date"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label={t('paid_to')}
            value={formData.paid_to}
            onChange={(e) => handleChange('paid_to', e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button onClick={handleSave} variant="contained">
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpensesPage;
